import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import type { MethodName } from "app/modules/api-client/apiClient";
import type { Args, Data } from "app/modules/api-client/queryTypes";

import { useKeywordListsNodeAPIQueryParams } from "../../../hooks/useKeywordListsNodeAPIQueryParams";
import { queryOptionsBuilder } from "../../../utils/queryOptionsBuilder";

const SEARCH_TRENDS_TAG = "DemandSearchTrends";

/**
 * Shared wrapper for the search-trends endpoints: resolves the keyword-list query params, builds the
 * query options for the given method + select, and runs the query. Cards calling the same method with
 * the same params share a single fetch (deduped by queryKey).
 */
export const useSearchTrendsQuery = <TMethod extends MethodName<typeof SEARCH_TRENDS_TAG>, TData>(
    methodName: TMethod,
    select: (data: Data<typeof SEARCH_TRENDS_TAG, TMethod>) => TData,
) => {
    const queryParams = useKeywordListsNodeAPIQueryParams();
    const queryOptions = useMemo(() => {
        // Every search-trends endpoint accepts the keyword-list params, but the per-method arg type
        // can't be proven for a generic method, so assert the (known-safe) tuple shape here.
        const args = [queryParams] as Args<typeof SEARCH_TRENDS_TAG, TMethod>;

        return queryOptionsBuilder(SEARCH_TRENDS_TAG, methodName)
            .setSelect(select)
            .setArgs(...args)
            .build();
    }, [methodName, select, queryParams]);

    return useQuery(queryOptions);
};
